/**
 * Created by Andrey Popov on 29.02.2024
 */

// the default logic of an offer: one product (by id, by payer level or a hard price) for one reward set.
// A config without `logic` gets it; the window that sells the offer is the config's `view`
const OfferLogic = function (offer) {
    this.offer = offer;
    this.config = Offers[offer.type];

    this.mission = this.config.mission;
    this.location = this.config.location;
    this.price = this.config.price;

    this.refresh();

    this.onPurchaseFinished = function () {};
};

OfferLogic.prototype.refresh = function () {
    this.payerLevel = cleverapps.paymentsHistory.classify();
    this.product = this.config.productByPayerLevel ? this.config.productByPayerLevel[this.payerLevel] : this.config.product;
    this.reward = this.config.rewardByPayerLevel ? this.config.rewardByPayerLevel[this.payerLevel] : this.config.reward;
};

OfferLogic.prototype.getConfig = function () {
    return this.config;
};

OfferLogic.prototype.getPayerLevel = function () {
    return this.payerLevel;
};

OfferLogic.prototype.getRewards = function () {
    return this.reward;
};

// the context names the offer: the purchase events carry it even for an offer built outside the Offers table
OfferLogic.prototype.getContext = function () {
    return {
        type: "mission_offer",
        offer: this.offer.type,
        name: this.offer.name
    };
};

OfferLogic.prototype.getProduct = function () {
    const context = this.getContext();

    if (this.product) {
        return Product.CreateById(this.product, context);
    }

    return Product.CreateByHardPrice(this.price, context);
};

OfferLogic.prototype.isIconAvailable = function () {
    let locationId;

    if (this.mission) {
        locationId = Missions[this.mission] && Missions[this.mission].location;
    } else if (this.location) {
        locationId = this.location;
    }

    return !locationId || locationId === cleverapps.meta.selectedLocationId();
};

OfferLogic.prototype.hasIconAttention = function () {
    return false;
};

OfferLogic.prototype.getPriceText = function () {
    return this.getProduct().getCurrentPrice();
};

OfferLogic.prototype.getTimeLeft = function () {
    const config = this.config;

    let timeLeft;

    if (config.duration) {
        timeLeft = Math.max(0, (this.offer.started || 0) + cleverapps.parseInterval(config.duration) - Date.now());
    } else if (config.feature) {
        const event = cleverapps.eventManager.getFeatureEvent(config.feature);
        timeLeft = Math.max(0, event && event.getTimeLeft() || 0);
    }

    // a mission offer ends with its mission, whatever else bounds it; otherwise a location offer ends with its location
    if (config.mission) {
        const mission = cleverapps.missionManager.findByType(config.mission);
        const missionTimeLeft = mission && mission.getTimeLeft() || 0;
        timeLeft = timeLeft === undefined ? missionTimeLeft : Math.min(timeLeft, missionTimeLeft);
    } else if (timeLeft === undefined && config.location) {
        timeLeft = cleverapps.meta.getLocationMeta(config.location).getTimeLeft();
    }

    return timeLeft || 0;
};

OfferLogic.prototype.buy = function () {
    const rewards = this.getRewards();

    // merge3 has nowhere to put units that do not fit; merge2 has no Map2d and sends them to the bubble stack
    if (rewards && rewards.unit && typeof Map2d !== "undefined" && Map2d.currentMap) {
        const unitsAmount = cleverapps.toArray(rewards.unit).reduce((sum, unit) => sum + (unit.amount || 0), 0);

        const notEnough = unitsAmount - Map2d.currentMap.countEmptySlots();
        if (notEnough > 0) {
            cleverapps.centerHint.createTextHint(Messages.get("Spawn.nospace", { left: notEnough }));
            return;
        }
    }

    const error = new RewardsList(rewards).checkReceiveRewards();
    if (error) {
        cleverapps.centerHint.createTextHint(error.message);
        return;
    }

    this.isBeingPurchased = true;

    this.getProduct().buy(this.processBought.bind(this), { noRewardWindow: true });
};

OfferLogic.prototype.processBought = function (success, boughtReward) {
    if (!success) {
        delete this.isBeingPurchased;
        this.onPurchaseFinished(false);
        return;
    }

    const rewards = boughtReward || this.getRewards();

    cleverapps.focusManager.display({
        focus: "RewardWindow",
        stack: true,
        action: (f) => {
            new RewardWindow(rewards, {
                toBubbleStack: true,
                event: cleverapps.EVENTS.EARN.PURCHASE
            });
            cleverapps.focusManager.onceNoWindowsListener = f;
        }
    });

    this.offer.complete();
    this.offer.logPurchase(this.getProduct());

    delete this.isBeingPurchased;

    this.onPurchaseFinished(true);
};

// a logic whose purchase reward only makes sense in place (a continue in the level) names a durable one for the restore
OfferLogic.RestoreBoughtReward = function (context) {
    const offer = Restorable.FindOffer(context);
    if (!offer) {
        return undefined;
    }
    return offer.logic.getRestoreRewards ? offer.logic.getRestoreRewards() : offer.logic.getRewards();
};

Restorable.BY_TYPE["mission_offer"] = new Restorable({
    location: Restorable.LocationOfferExpedition,
    find: Restorable.FindOffer,
    give: Restorable.GiveOffer,
    boughtReward: OfferLogic.RestoreBoughtReward
});
